<template>
  <div class="relative inline-block text-left dropdown-container" ref="dropdown">
    <!-- Category Button -->
    <button ref="categoryButton" @click.stop="handleCategoryClick" type="button"
      :class="['inline-flex justify-center category-button whitespace-nowrap w-full px-4 py-2 text-sm font-medium text-gray-700 border-2 rounded-lg', getColor(50, 'bg'), getColor(200, 'bg'), getColor(400, 'border')]">
      {{ categoryData.name }}
      <font-awesome-icon v-if="hasSubCategories" :icon="['fas', 'chevron-down']" :class="{ 'rotate-180': isOpen }"
        class="h-3 w-3 ml-2 mt-1 transition-transform duration-200" />
    </button>

    <!-- Dropdown Menu -->
    <teleport :to="teleportTarget">
      <div v-if="isOpen && hasSubCategories" :style="dropdownStyle"
        class="flex flex-col justify-center whitespace-nowrap w-auto bg-white rounded-md shadow-sm text-gray-700 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-gray-100 focus:ring-blue-500 mt-2  z-50 border border-gray-300"
        @click.stop>
        <div v-for="subCategory in categoryData.categories" :key="subCategory.id" class="relative">
          <!-- Subcategory Button -->
          <button @click.stop="selectCategoryOrToggle(subCategory)"
            class="flex justify-between items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            type="button">
            {{ subCategory.name }}
            <font-awesome-icon v-if="subCategoryHasSubcategories(subCategory)" :icon="['fas', 'chevron-right']"
              :class="{ 'rotate-90': activeSubcategory && activeSubcategory.id === subCategory.id }"
              class="h-3 w-3 ml-2 transition-transform duration-200" />
          </button>

          <!-- Recursive Subcategory Dropdown -->
          <SubcategoryDropdown v-if="activeSubcategory && activeSubcategory.id === subCategory.id"
            :categories="subCategory.categories" @categorySelected="selectSubCategory"
            @resetDropdownStates="resetDropdownStates" />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SubcategoryDropdown from './SubcategoryDropdown.vue';

const props = defineProps({
  category: Object,
  globalOpenCategory: Number,
  getColor: {
    type: Function,
    required: false,
    default: (shade = 500, prefix = 'text') => `${prefix}-${shade}`
  },
});

const emit = defineEmits(['categorySelected', 'updateGlobalOpenCategory', 'resetDropdownStates']);

const isOpen = ref(false);
const activeSubcategory = ref(null);
const dropdown = ref(null);
const categoryButton = ref(null);
const dropdownStyle = ref({});

const categoryData = computed(() => props.category);

// Adjusted to handle cases where categories can be false, null, or undefined
const hasSubCategories = computed(
  () => Array.isArray(categoryData.value.categories) && categoryData.value.categories.length > 0
);

// Function to check if a subcategory has further subcategories
const subCategoryHasSubcategories = (subCategory) =>
  Array.isArray(subCategory.categories) && subCategory.categories.length > 0;

const teleportTarget = computed(() => {
  // In development (localhost), always use '#app'
  if (process.env.NODE_ENV === 'development') {
    return '#app';
  }
  // In production, use vueAppConfig's element_id or fallback to '#app'
  return window.vueAppConfig?.settings?.element_id ?? '#app';
});

watch(
  () => props.globalOpenCategory,
  (newVal) => {
    isOpen.value = newVal === categoryData.value.id;
    if (!isOpen.value) {
      activeSubcategory.value = null;
    }
  }
);

const closeDropdownOnResize = () => {
  isOpen.value = false;
  activeSubcategory.value = null;
  emit('updateGlobalOpenCategory', null);
};

const handleCategoryClick = () => {
  if (hasSubCategories.value) {
    const newState = isOpen.value ? null : categoryData.value.id;
    emit('updateGlobalOpenCategory', newState);
    isOpen.value = !isOpen.value;

    if (isOpen.value) {
      nextTick(() => {
        const buttonRect = categoryButton.value.getBoundingClientRect();
        dropdownStyle.value = {
          position: 'absolute',
          top: `${buttonRect.bottom + window.scrollY}px`,
          left: `${buttonRect.left + window.scrollX}px`,
          minWidth: `${buttonRect.width}px`,
          zIndex: 9999,
        };
      });
    }
  } else {
    isOpen.value = false;
    emit('updateGlobalOpenCategory', null);
    emit('categorySelected', categoryData.value);
  }
};


// Select category or toggle subcategory dropdown
const selectCategoryOrToggle = (subCategory) => {
  const hasSubCategories = subCategoryHasSubcategories(subCategory);

  if (hasSubCategories) {
    // Open if not active or active with different subcategory
    if (!activeSubcategory.value || activeSubcategory.value.id !== subCategory.id) {
      activeSubcategory.value = subCategory;
      isOpen.value = true; // Ensure the main dropdown remains open
    } else {
      // Toggle the subcategory dropdown off
      activeSubcategory.value = null;
      isOpen.value = true; // Keep the main dropdown open
    }
  } else {
    activeSubcategory.value = null; // Reset active subcategory
    isOpen.value = false; // Close all dropdowns
    emit('updateGlobalOpenCategory', null);
    emit('categorySelected', subCategory);
  }
};

// Handle subcategory selection with subcategories check
const selectSubCategory = (subCategory) => {
  const hasSubCategories = subCategoryHasSubcategories(subCategory);

  if (hasSubCategories) {
    // Keep dropdown open and show subcategories
    activeSubcategory.value = subCategory; // Set active subcategory to manage dropdown visibility
    isOpen.value = true; // Keep the dropdown open
  } else {
    // If no further subcategories, close all dropdowns
    activeSubcategory.value = null;
    isOpen.value = false;
    emit('categorySelected', subCategory);
    emit('updateGlobalOpenCategory', null);
    emit('resetDropdownStates'); // Emit reset event to handle global state reset
  }
};

// Reset state when receiving reset event
const resetDropdownStates = () => {
  if (activeSubcategory.value) {
    activeSubcategory.value = null;
  }
};

// Handle outside click to close dropdowns
const handleClickOutside = (event) => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    resetDropdownStates();
    emit('updateGlobalOpenCategory', null);
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', closeDropdownOnResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', closeDropdownOnResize);
});
</script>

<style scoped>
.category-button:hover {
  transform: scale(1.05);
  transition: transform 0.2s;

}

.dropdown-container {
  display: inline-block;
}

.rotate-180 {
  transform: rotate(180deg);
}

.rotate-90 {
  transform: rotate(90deg);
}
</style>